import React, { useEffect, useRef, useState } from "react";
import { Award, ArrowRight, Users, Calendar } from "lucide-react";
import { Button } from "./ui/button";
import { trackCTAClick } from "../utils/analytics";

export const HeroCRO = ({ onEnrollClick }) => {
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        const heroHeight = rect.height;
        const scrollPosition = window.scrollY;

        // Only apply parallax when hero is in view
        if (scrollPosition < heroHeight) {
          setScrollY(scrollPosition);
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial call

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden -mt-20 pt-20"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {/* Mobile Image */}
        <div
          className="md:hidden w-full h-full"
          style={{
            transform: `translateY(${scrollY * 0.5}px) scale(1.1)`,
            transition: "transform 0.1s ease-out",
          }}
        >
          <img
            src="/images/BJ-Mobile-Hero.jpg"
            alt="Acting Training"
            className="w-full h-full object-cover"
          />
        </div>
        {/* Desktop Image */}
        <div
          className="hidden md:block w-full h-full"
          style={{
            transform: `translateY(${scrollY * 0.5}px) scale(1.1)`,
            transition: "transform 0.1s ease-out",
          }}
        >
          <img
            src="/images/bjlp-hero.jpg"
            alt="Acting Training"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/85 to-gray-900/90"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
        <div className="max-w-3xl lg:max-w-4xl mx-auto lg:mx-0 text-center lg:text-left">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-purple-500/20 backdrop-blur-md border border-purple-300/40 rounded-full mb-4 sm:mb-6">
            <Award className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-300" />
            <span className="text-xs sm:text-sm font-semibold text-purple-100">
              Train with Padma Shri Awardee Barry John
            </span>
          </div>

          {/* Main Headline */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight tracking-tight">
            Fly High With{" "}
            <span className="block mt-1 sm:mt-2 bg-gradient-to-r from-purple-400 via-purple-300 to-pink-300 bg-clip-text text-transparent">
              The Most Advanced
            </span>
            <span className="block mt-1 sm:mt-2 text-2xl sm:text-3xl md:text-4xl lg:text-5xl">
              Acting Programmes
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-200 mb-6 sm:mb-8 leading-relaxed px-2 sm:px-0 lg:px-0 max-w-2xl lg:max-w-none mx-auto lg:mx-0">
            Learn from <span className="font-bold text-white">Barry John</span>{" "}
            exclusively at The Free Birds Collective, Mumbai. Advanced,
            practical training that prepares you for{" "}
            <span className="font-semibold text-purple-300">
              real professional work
            </span>{" "}
            in film and theatre.
          </p>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center lg:justify-start gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
              <span className="text-gray-200 text-sm sm:text-base">
                3000+ Alumni
              </span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
              <span className="text-gray-200 text-sm sm:text-base">
                50+ Years Experience
              </span>
            </div>
          </div>

          {/* Urgency */}
          <div className="bg-purple-500/10 backdrop-blur-sm border border-purple-300/30 rounded-xl px-4 py-4 sm:px-6 sm:py-4 max-w-2xl lg:max-w-none mx-auto lg:mx-0">
            {/* Mobile Layout */}
            <div className="block sm:hidden space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-lg">⚡</span>
                <span className="text-purple-200 text-sm font-semibold">
                  Next Batch Starting
                </span>
              </div>
              <div className="space-y-2 pl-7">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                  <span className="text-purple-200 text-sm">Diploma:</span>
                  <span className="font-bold text-white text-sm">
                    Nov 16th, 2026
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                  <span className="text-purple-200 text-sm">Weekend:</span>
                  <span className="font-bold text-white text-sm">
                    Nov 14th, 2026
                  </span>
                </div>
              </div>
              <div className="pt-2 border-t border-purple-300/20 pl-7">
                <span className="text-purple-200 text-xs font-medium">
                  Only 20 seats per batch
                </span>
              </div>
            </div>

            {/* Desktop Layout */}
            <p className="hidden sm:block text-purple-200 text-sm font-medium leading-relaxed">
              <span>⚡ Next Batch:</span>{" "}
              <span className="font-bold text-white">Nov 16th, 2026</span>{" "}
              (Diploma) •{" "}
              <span className="font-bold text-white">14th Nov, 2026</span>{" "}
              (Weekend) • <span>Only 20 seats per batch</span>
            </p>
          </div>
        </div>
      </div>

      {/* Scroll Indicator - Mobile Optimized */}
      <div className="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-white/40 rounded-full flex items-start justify-center p-1.5 sm:p-2">
          <div className="w-1 h-2 sm:w-1.5 sm:h-3 bg-white/70 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};
